// src/components/CrisisGuidance.tsx
//
// P2.13C: the approved crisis / urgent-help guidance surface. ALL visible
// wording here was human-approved (clinical + operations, P2.13B) and must
// be changed only through that process — hence no free-text props: pages can
// pick a variant, nothing else, so placements cannot drift from the approved
// copy.
//
// Server-compatible by design: no hooks, no browser APIs, and — as a hard
// requirement — NO analytics of any kind. tel:112 / tel:14416 are plain
// semantic anchors; a click on an emergency or mental-health support number
// must never be intentionally reported to GA4/Ads.
//
// Visual intent: clearly distinguishable from ReACH booking/contact CTAs
// (calm warm notice surface, not the emerald CTA language, no alarmist
// styling) so the government resources never read as another way to reach
// the clinic.

import { EMERGENCY_112, TELE_MANAS } from '@/lib/crisis-resources'

type CrisisGuidanceVariant = 'compact' | 'card' | 'assessment'

interface CrisisGuidanceProps {
  variant: CrisisGuidanceVariant
}

/** The two approved guidance sentences, shared by every variant. */
function ResourceLines({ light = false }: { light?: boolean }) {
  const text = light ? 'text-gray-200' : 'text-gray-800'
  const link = light
    ? 'font-semibold text-white underline underline-offset-4'
    : 'font-semibold text-gray-900 underline underline-offset-4'
  return (
    <>
      <p className={`text-sm sm:text-base ${text}`}>
        If you or someone else is in immediate danger, call{' '}
        <a href={EMERGENCY_112.href} className={link}>
          {EMERGENCY_112.displayNumber}
        </a>
        .
      </p>
      <p className={`text-sm sm:text-base ${text}`}>
        For 24×7 mental-health support in India, call Tele-MANAS at{' '}
        <a href={TELE_MANAS.href} className={link}>
          {TELE_MANAS.displayNumber}
        </a>
        .
      </p>
    </>
  )
}

const CrisisGuidance: React.FC<CrisisGuidanceProps> = ({ variant }) => {
  if (variant === 'compact') {
    // Footer variant: sits on the dark footer surface, visually separated
    // from the clinic contact rows by its own bordered block.
    return (
      <div
        aria-label="Urgent help information"
        className="w-full rounded-2xl border border-white/25 bg-white/5 px-5 py-4 sm:px-6 sm:py-5 flex flex-col gap-1.5"
      >
        <ResourceLines light />
      </div>
    )
  }

  if (variant === 'assessment') {
    // Assessment-tool variant: contextual note, no extra heading (the page
    // supplies its own section semantics); aria-label for accessibility.
    return (
      <div
        aria-label="Urgent help information"
        className="rounded-lg sm:rounded-xl border border-yellow-300 bg-yellow-50 p-4 sm:p-6 flex flex-col gap-1.5"
      >
        <p className="text-sm sm:text-base font-semibold text-gray-900">
          These screening tools are not for emergencies.
        </p>
        <ResourceLines />
      </div>
    )
  }

  // 'card': the standard urgent-help card for /book and service pages.
  return (
    <section
      aria-labelledby="crisis-guidance-heading"
      className="px-4 py-6 sm:py-8"
    >
      <div className="mx-auto max-w-3xl rounded-xl sm:rounded-2xl border border-yellow-300 bg-yellow-50 px-5 py-5 sm:px-8 sm:py-6 flex flex-col gap-2">
        <h2
          id="crisis-guidance-heading"
          className="text-lg sm:text-xl font-semibold text-gray-900"
        >
          Need urgent help?
        </h2>
        <p className="text-sm sm:text-base text-gray-800">
          ReACH appointment booking is for routine and ongoing care.
        </p>
        <ResourceLines />
      </div>
    </section>
  )
}

export default CrisisGuidance
